import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaPlus, FaMinus } from "react-icons/fa";
import { useDispatch } from "react-redux";
import {
  setDrillDownDistrictCode,
  setDrillDownTalukCode,
  setTableChartId,
  setTableHeading,
  setTalukCode,
} from "../../applicationStatus/store";
import DynamicLoader from "../../Form/FormFields/DynamicLoader";
import Level3Table from "./Level3Table";
import {
  Level2TableProps,
  removeZeroColumns,
  convertColumns,
  renderCellValue,
} from "./tableUtils";

const Level2Table: React.FC<Level2TableProps> = ({
  selectorData,
  level2ChartId,
  level3ChartId,
  subData,
  dispatch: externalDispatch,
  id,
  applicationStatusData,
  fetchPopupData,
  globalFilter,
}) => {
  const dispatch = externalDispatch ?? useDispatch();

  // ── Level-2 own state ──
  const [expandedTalukRow, setExpandedTalukRow] = useState<number | null>(null);

  // ── Derive data from Redux selector ──
  const dd = level2ChartId
    ? (selectorData as any)?.[level2ChartId as any]
    : null;
  const effectiveLevel3ChartId = dd?.drill_down_chart_id || level3ChartId;
  const cols = dd?.data?.columns || dd?.columns || [];
  const isLoading = level2ChartId
    ? selectorData.loaders[`report/chart/${level2ChartId}`]
    : false;

  // ── Build tanstack columns ──
  const formattedCols = React.useMemo(() => {
    const filtered = removeZeroColumns(subData, cols);
    return convertColumns(filtered);
  }, [dd, subData]);

  const table = useReactTable({
    data: subData,
    columns: formattedCols,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { globalFilter },
    onGlobalFilterChange: () => {},
  });

  // ── Taluk expand handler ──
  const handleTalukExpand = async (rowIndex: number, row: any) => {
    if (expandedTalukRow === rowIndex) {
      setExpandedTalukRow(null);
      await dispatch(setDrillDownTalukCode(""));
    } else {
      setExpandedTalukRow(rowIndex);
      // Clear leftover popup-only filters (taluk code, district code, column)
      // from any earlier popup click, so they don't leak into this plain
      // drilldown fetch.
      await dispatch(setTalukCode(""));
      await dispatch(setTableChartId(""));
      await dispatch(setTableHeading({ id, accessorKey: "" } as any));
      if (row?.taluk_code) {
        await dispatch(setDrillDownTalukCode(row.taluk_code));
      }
      if (applicationStatusData && effectiveLevel3ChartId !== undefined) {
        await applicationStatusData(Number(effectiveLevel3ChartId));
      }
    }
  };

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <DynamicLoader />
      </div>
    );
  }

  // ── Empty state ──
  if (!dd || subData.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 font-medium">
        No taluk data available
      </div>
    );
  }

  return (
    <table className="w-full text-left border-separate border-spacing-0">
      <thead className="bg-blue-800 text-blue-100 text-center sticky top-0 z-[2]">
        {table.getHeaderGroups().map((hg) => (
          <tr key={hg.id}>
            {hg.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                className="sticky top-0 z-[2] px-6 py-3 text-[13px] font-semibold uppercase tracking-wider border-b border-blue-700 text-nowrap bg-blue-800 bg-clip-padding"
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, rowIndex) => {
          const original = row.original as any;
          const canDrillIn =
            effectiveLevel3ChartId !== undefined &&
            Number(original?.expand) === 1;

          return (
            <React.Fragment key={rowIndex}>
              <tr className="border-b border-white text-center hover:bg-slate-50 transition-colors">
                {row.getVisibleCells().map((cell, cellIndex) => {
                  const accessorKey = (cell.column.columnDef as any)
                    .accessorKey;

                  // ── expand column ──
                  if (cell.column.id === "expand") {
                    return (
                      <td
                        key={cellIndex}
                        className="px-4 py-3 text-[13px] text-center"
                      >
                        {canDrillIn ? (
                          <button
                            onClick={() =>
                              handleTalukExpand(rowIndex, original)
                            }
                            className={`p-1.5 rounded-lg transition-all duration-200 ${
                              expandedTalukRow === rowIndex
                                ? "bg-red-50 text-red-600 hover:bg-red-100"
                                : "bg-green-50 text-green-700 hover:bg-green-100"
                            } shadow-sm border border-transparent hover:border-current/20`}
                          >
                            {expandedTalukRow === rowIndex ? (
                              <FaMinus size={11} title="Collapse villages" />
                            ) : (
                              <FaPlus size={11} title="Expand villages" />
                            )}
                          </button>
                        ) : null}
                      </td>
                    );
                  }

                  // ── regular data column ──
                  return (
                    <td
                      key={cellIndex}
                      className="px-4 py-3 text-[13px] cursor-pointer text-slate-600"
                      onClick={async () => {
                        await dispatch(setDrillDownDistrictCode(""));
                        await dispatch(
                          setTableHeading({ id, accessorKey } as any),
                        );
                        if (original?.taluk_code) {
                          await dispatch(setTalukCode(original.taluk_code));
                        }
                        // taluk_code alone is not unique across districts, so
                        // the district code must also be sent or the popup
                        // query matches the same taluk_code in every district.
                        if (original?.district_code) {
                          await dispatch(
                            setTableChartId(original.district_code),
                          );
                        }
                        if (fetchPopupData) fetchPopupData(original, "taluk");
                      }}
                    >
                      {renderCellValue(cell)}
                    </td>
                  );
                })}
              </tr>

              {/* Level-3 village drilldown */}
              {expandedTalukRow === rowIndex && (
                <tr>
                  <td
                    colSpan={formattedCols.length + 2}
                    className="px-6 py-4 bg-purple-50/60 border-b border-purple-100"
                  >
                    <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
                      <Level3Table
                        selectorData={selectorData}
                        chartId={effectiveLevel3ChartId}
                        dispatch={dispatch}
                        id={id}
                        fetchPopupData={fetchPopupData}
                      />
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export default Level2Table;
